import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useDataProvider, useGetList, useNotify, useTranslate } from "ra-core";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { TemplateEditor } from "./TemplateEditor";
import {
  keyFromName,
  type ChecklistTemplate,
  type TemplateItem,
  useChecklistTemplates,
} from "./taskData";

// Checklist templates (task 1.3, D-030), for the whole team: list, add,
// open the editor. No delete: a template in use is turned off.
export const TaskTemplatesDialog = ({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) => {
  const translate = useTranslate();
  const notify = useNotify();
  const dataProvider = useDataProvider();
  const queryClient = useQueryClient();
  const { data: templates = [] } = useChecklistTemplates();
  const { data: items = [] } = useGetList<TemplateItem>(
    "checklist_template_items",
    {
      pagination: { page: 1, perPage: 5000 },
      sort: { field: "id", order: "ASC" },
    },
    { enabled: open },
  );
  const [newName, setNewName] = useState("");
  const [editedId, setEditedId] = useState<number | null>(null);
  const edited = templates.find((template) => template.id === editedId) ?? null;

  const add = async (event: React.FormEvent) => {
    event.preventDefault();
    const name = newName.trim();
    if (!name) return;
    try {
      const { data } = await dataProvider.create<ChecklistTemplate>(
        "checklist_templates",
        { data: { key: keyFromName(name), name, active: true } },
      );
      setNewName("");
      await queryClient.invalidateQueries({
        queryKey: ["checklist_templates"],
      });
      setEditedId(data.id);
    } catch (error) {
      notify(error instanceof Error ? error.message : String(error), {
        type: "error",
      });
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-h-[90dvh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {translate("tenax.tasks.templates.title")}
            </DialogTitle>
            <DialogDescription>
              {translate("tenax.tasks.templates.hint")}
            </DialogDescription>
          </DialogHeader>
          <ul className="flex flex-col divide-y rounded-md border">
            {templates.map((template) => (
              <li key={template.id}>
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 px-3 py-3 text-left"
                  onClick={() => setEditedId(template.id)}
                >
                  <span
                    className={
                      template.active ? "font-medium" : "text-muted-foreground"
                    }
                  >
                    {template.name}
                  </span>
                  <span className="text-muted-foreground flex items-center gap-2 text-xs">
                    {!template.active && (
                      <Badge variant="secondary">
                        {translate("tenax.tasks.templates.inactive")}
                      </Badge>
                    )}
                    {translate("tenax.tasks.templates.steps_count", {
                      count: items.filter(
                        (item) => item.template_id === template.id,
                      ).length,
                    })}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <form onSubmit={add} className="flex gap-2">
            <Input
              value={newName}
              placeholder={translate("tenax.tasks.templates.new_name")}
              aria-label={translate("tenax.tasks.templates.new_name")}
              onChange={(event) => setNewName(event.target.value)}
            />
            <Button type="submit" disabled={!newName.trim()}>
              {translate("tenax.tasks.templates.add")}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
      <TemplateEditor
        template={edited}
        onOpenChange={(isOpen) => !isOpen && setEditedId(null)}
      />
    </>
  );
};
